import { IonIcon } from '@ionic/react';
import { hourglass } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';

import { GET_UPCOMING_MATCH_WEEK } from '../../../../graphql/queries/matchWeek'; // ajusta ruta real
import type { GetUpcomingMatchWeekQuery } from '../../../../types/graphql-generated';

import { msUntilMadridStartOfDay, formatCountdown } from '../../../../utils/time';

interface TimeInfo {
  label: string;
  countdown: string | null;
  color: 'red' | 'orange' | 'yellow' | 'green' | 'gray';
  showTimer: boolean;
}

export const LineupTimeCounter = () => {
  const { data, loading, error } = useQuery<GetUpcomingMatchWeekQuery>(GET_UPCOMING_MATCH_WEEK);

  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 100);
    return () => clearInterval(interval);
  }, []);

  const timeInfo = useMemo(() => {
    return getTimeInfo({ data, loading, error, nowMs });
  }, [data, loading, error, nowMs]);

  const colorClasses = {
    red: 'text-red-600 bg-red-50',
    orange: 'text-orange-600 bg-orange-50',
    yellow: 'text-yellow-600 bg-yellow-50',
    green: 'text-green-600 bg-green-50',
    gray: 'text-gray-600 bg-gray-50',
  };

  return (
    <div className={`w-full mb-4 p-3 rounded-lg ${colorClasses[timeInfo.color]}`}>
      <p className="text-sm font-semibold text-center">{timeInfo.label}</p>
      <div className="flex items-center justify-center gap-2 mt-1">
        {timeInfo.showTimer && <IonIcon icon={hourglass} className="text-lg" />}
        <p className="text-base font-bold text-center">{timeInfo.countdown || '---'}</p>
      </div>
    </div>
  );
};

function getTimeInfo({
  data,
  loading,
  error,
  nowMs,
}: {
  data: GetUpcomingMatchWeekQuery | undefined;
  loading: boolean;
  error: unknown;
  nowMs: number;
}): TimeInfo {
  if (loading) {
    return { label: 'Cargando próxima Jornada', countdown: null, color: 'gray', showTimer: false };
  }

  if (error) {
    return { label: 'No se pudo cargar la próxima Jornada', countdown: null, color: 'gray', showTimer: false };
  }

  const mw = data?.getUpcomingMatchWeek;
  const startDate = mw?.startDate;
  const weekNumber = mw?.weekNumber;

  if (!startDate) {
    return { label: 'No hay próxima Jornada', countdown: null, color: 'gray', showTimer: false };
  }

  const msUntil = msUntilMadridStartOfDay(startDate, nowMs);

  if (msUntil <= 0) {
    return {
      label: weekNumber ? `La Jornada ${weekNumber} ya ha comenzado` : 'La Jornada ya ha comenzado',
      countdown: null,
      color: 'gray',
      showTimer: false,
    };
  }

  const countdown = formatCountdown(msUntil);

  const hoursRemaining = msUntil / (1000 * 60 * 60);
  let color: TimeInfo['color'] = 'green';

  if (hoursRemaining < 6) color = 'red';
  else if (hoursRemaining < 12) color = 'orange';
  else if (hoursRemaining < 24) color = 'yellow';

  return {
    label: weekNumber ? `Tiempo para el inicio de la Jornada ${weekNumber}` : 'Tiempo para el inicio de la próxima Jornada',
    countdown,
    color,
    showTimer: true,
  };
}

